/**
 * Bundle Package Confirmations
 * Consolidated confirmation screens for all package tiers
 */

window.BundleConfirmations = (function() {
  'use strict';

  /**
   * Render a package confirmation screen
   * @param {string} tier - 'basic', 'premium', or 'elite'
   * @param {object} config - Bundle configuration object
   * @param {object} customBundleSelections - Current selections
   * @param {object} numberPlateConfig - Number plate configuration
   * @param {function} getEligibleAddonsForPackage - Function to get eligible addons
   * @returns {string} HTML for confirmation screen
   */
  function renderConfirmation(tier, config, customBundleSelections, numberPlateConfig, getEligibleAddonsForPackage) {
    // Get tier-specific configuration
    const tierConfig = getTierConfig(tier, config);

    // Get addons for this tier
    let allSelections = [];
    let eligibleAddons = [];

    if (tier === 'basic') {
      // Basic tier uses custom selections
      for (const [category, items] of Object.entries(customBundleSelections)) {
        if (Array.isArray(items)) {
          allSelections.push(...items);
        }
      }
    } else {
      // Premium and Elite use pre-built packages
      const packageConfig = config.preBuiltBundles[tier === 'premium' ? 'premium' : 'elite'];
      eligibleAddons = getEligibleAddonsForPackage(packageConfig.includes);
      allSelections = eligibleAddons;
    }

    // Calculate pricing
    const pricing = calculatePricing(tier, allSelections);

    // Check configuration status
    const numberPlateAddon = allSelections.find(a => a.handle === 'number-plate-addon');
    const numberPlateConfigured = numberPlateConfig && numberPlateConfig.front && numberPlateConfig.back;
    const letterConfigured = window.letterWizardState && window.letterWizardState.configured;

    // Determine what needs configuration
    const needsNumberPlate = tier !== 'basic' && numberPlateAddon && !numberPlateConfigured;
    const needsLetter = !letterConfigured;
    const allConfigured = !needsNumberPlate && !needsLetter;

    return `
      <div class="bundle-screen active">
        <div class="package-confirmation">
          <div class="package-confirmation__header">
            <span class="tier-badge tier-badge--${tier} active">${tierConfig.badge}</span>
            <h2 class="package-confirmation__title">${tierConfig.title}</h2>
            <p class="package-confirmation__description">${tierConfig.description}</p>
          </div>

          <div class="package-confirmation__content">
            <div class="package-confirmation__includes">
              <h3 class="package-confirmation__section-title">What's Included</h3>
              <ul class="package-includes-list">
                ${renderIncludedItems(allSelections, tier, numberPlateConfigured, letterConfigured)}
                ${renderEmptyState(allSelections, tier)}
              </ul>
            </div>

            ${renderWarning(allConfigured, needsNumberPlate, needsLetter, tier)}

            <div class="package-confirmation__pricing">
              ${renderPricingRows(pricing, tier)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function getTierConfig(tier) {
    const configs = {
      basic: {
        badge: 'BASIC',
        title: 'Included FREE',
        description: 'Every ride-on includes a magical personalised letter from Santa',
        discount: 0
      },
      premium: {
        badge: 'PREMIUM',
        title: 'Premium Package',
        description: 'Enhanced performance and personalisation',
        discount: 0.15
      },
      elite: {
        badge: 'ELITE',
        title: 'Elite Package',
        description: 'The complete ride-on experience with everything included',
        discount: 0.30
      }
    };
    return configs[tier] || configs.basic;
  }

  function calculatePricing(tier, addons) {
    let subtotal = 0;
    addons.forEach(addon => subtotal += (addon.price || 0));

    const tierConfig = getTierConfig(tier);
    const discount = subtotal * tierConfig.discount;
    const total = subtotal - discount;

    return {
      subtotal: subtotal,
      discount: discount,
      discountPercent: tierConfig.discount * 100,
      total: total
    };
  }

  function renderIncludedItems(addons, tier, numberPlateConfigured, letterConfigured) {
    if (addons.length === 0 && tier === 'basic') {
      // Empty state for basic tier
      return '';
    }

    return addons.map(addon => {
      let configStatus = '';
      let configButton = '';

      if (addon.handle === 'number-plate-addon' && tier !== 'basic') {
        configStatus = numberPlateConfigured ?
          '<span class="config-status config-status--configured">✓ Configured</span>' :
          '<span class="config-status config-status--pending">⚠ Required</span>';
        configButton = `
          <button class="button ${numberPlateConfigured ? 'button--secondary' : 'button--primary'} button--sm" is="hover-button"
                  onclick="customiseNumberPlateInPackage('${tier}')">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">${numberPlateConfigured ? 'Edit' : 'Customise'}</span>
          </button>
        `;
      }

      if (addon.handle === 'personalised-letter') {
        configStatus = letterConfigured ?
          '<span class="config-status config-status--configured">✓ Configured</span>' :
          '<span class="config-status config-status--pending">⚠ Required</span>';
        configButton = `
          <button class="button ${letterConfigured ? 'button--secondary' : 'button--primary'} button--sm" is="hover-button"
                  onclick="customiseLetterInPackage('${tier}')">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">${letterConfigured ? 'Edit' : 'Customise'}</span>
          </button>
        `;
      }

      const needsCustomization = configButton !== '';

      // Get variant display name (only if multiple variants)
      let variantName = '';
      if (addon.hasVariants && addon.variants && addon.variants.length > 1) {
        const selectedVariantId = addon.variantId;
        const variant = addon.variants.find(v => v.id == selectedVariantId);
        if (variant && variant.title) {
          variantName = ` (${variant.title})`;
        }
      }

      return `
        <li class="package-includes-item">
          <div class="package-includes-main">
            <span class="package-includes-checkmark">✓</span>
            <span class="package-includes-text">${addon.name}${variantName}</span>
          </div>
          ${needsCustomization ? `
            <div class="package-includes-actions">
              ${configStatus}
              ${configButton}
            </div>
          ` : ''}
        </li>
      `;
    }).join('');
  }

  function renderEmptyState(addons, tier) {
    if (addons.length === 0 && tier === 'basic') {
      const letterConfigured = window.letterWizardState && window.letterWizardState.configured;
      return `
        <li class="package-includes-item">
          <div class="package-includes-main">
            <span class="package-includes-checkmark">✓</span>
            <span class="package-includes-text">Free personalised Santa letter</span>
          </div>
          <div class="package-includes-actions">
            <span class="config-status config-status--${letterConfigured ? 'configured' : 'pending'}">
              ${letterConfigured ? '✓ Configured' : '⚠ Required'}
            </span>
            <button class="button button--${letterConfigured ? 'secondary' : 'primary'} button--sm" is="hover-button"
                    onclick="customiseLetterInPackage('basic')">
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">${letterConfigured ? 'Edit' : 'Customise'}</span>
            </button>
          </div>
        </li>
      `;
    }
    return '';
  }

  function renderWarning(allConfigured, needsNumberPlate, needsLetter, tier) {
    if (allConfigured) return '';

    let warningText = '';
    if (needsNumberPlate && needsLetter) {
      warningText = 'Please customise the number plate and letter before adding to cart';
    } else if (needsNumberPlate) {
      warningText = 'Please customise the number plate before adding to cart';
    } else if (needsLetter) {
      warningText = tier === 'basic'
        ? 'Please customise the Santa letter before adding to cart'
        : 'Please customise the letter before adding to cart';
    }

    return `
      <div class="package-confirmation__warning">
        <span class="package-warning-icon">⚠</span>
        <p class="package-warning-text">${warningText}</p>
      </div>
    `;
  }

  function renderPricingRows(pricing, tier) {
    if (tier === 'basic') {
      return `
        <div class="package-price-row package-price-row--total">
          <span class="package-price-label">Package Total</span>
          <span class="package-price-value package-price-value--total">
            £0 <small class="package-price-subtext">(included free)</small>
          </span>
        </div>
      `;
    }

    const rows = [];

    // Subtotal row
    rows.push(`
      <div class="package-price-row">
        <span class="package-price-label">Items Total</span>
        <span class="package-price-value">£${pricing.subtotal.toFixed(2)}</span>
      </div>
    `);

    // Discount row
    if (pricing.discount > 0) {
      rows.push(`
        <div class="package-price-row package-price-row--discount">
          <span class="package-price-label">Bundle Discount (${pricing.discountPercent}%)</span>
          <span class="package-price-value package-price-value--savings">-£${pricing.discount.toFixed(2)}</span>
        </div>
      `);
    }

    // Total row
    rows.push(`
      <div class="package-price-row package-price-row--total">
        <span class="package-price-label">Package Total</span>
        <span class="package-price-value package-price-value--total">£${pricing.total.toFixed(2)}</span>
      </div>
    `);

    return rows.join('');
  }

  // Public API
  return {
    renderConfirmation: renderConfirmation
  };
})();